import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { BodyMetricLogDto } from '@wellverse/shared-ts';
import { BodyMetricType, BodyMetricSourceType } from '@wellverse/shared-ts';

const metricColors: Record<BodyMetricType, string> = {
  [BodyMetricType.WEIGHT]: '#1890ff',
  [BodyMetricType.HEIGHT]: '#722ed1',
  [BodyMetricType.BODY_FAT]: '#f5a623',
  [BodyMetricType.MUSCLE_MASS]: '#3ecf8e',
  [BodyMetricType.BONE_MASS]: '#13c2c2',
  [BodyMetricType.WATER_PERCENTAGE]: '#1677ff',
  [BodyMetricType.BMI]: '#eb2f96',
  [BodyMetricType.WAIST_CIRCUMFERENCE]: '#fa541c',
  [BodyMetricType.HIP_CIRCUMFERENCE]: '#a0d911',
  [BodyMetricType.CHEST_CIRCUMFERENCE]: '#2f54d1',
  [BodyMetricType.BICEP_CIRCUMFERENCE]: '#d9d9d9',
  [BodyMetricType.THIGH_CIRCUMFERENCE]: '#b37feb',
  [BodyMetricType.VISCERAL_FAT]: '#ff4d4f',
  [BodyMetricType.BMR]: '#fadb14',
  [BodyMetricType.BODY_AGE]: '#52c41a',
  [BodyMetricType.HEART_RATE]: '#ff6b6b',
  [BodyMetricType.BLOOD_PRESSURE_SYS]: '#13a8a8',
  [BodyMetricType.BLOOD_PRESSURE_DIA]: '#0e7090',
  [BodyMetricType.SYMPTOM]: '#9254de',
};

const sourceColors: Record<BodyMetricSourceType, string> = {
  [BodyMetricSourceType.MANUAL]: '#898989',
  [BodyMetricSourceType.APPLE_HEALTH]: '#52c41a',
  [BodyMetricSourceType.GOOGLE_FIT]: '#1890ff',
  [BodyMetricSourceType.GARMIN]: '#007cc3',
  [BodyMetricSourceType.FITBIT]: '#00b0b9',
  [BodyMetricSourceType.SAMSUNG_HEALTH]: '#1428a0',
  [BodyMetricSourceType.WITHINGS]: '#4bc9c0',
  [BodyMetricSourceType.XIAOMI]: '#ff6900',
  [BodyMetricSourceType.RENPHO]: '#7b2cbf',
  [BodyMetricSourceType.OPPO]: '#6b2737',
  [BodyMetricSourceType.HUAWEI_HEALTH]: '#cf222e',
  [BodyMetricSourceType.APPLE_WATCH]: '#000000',
  [BodyMetricSourceType.GARMIN_WATCH]: '#007cc3',
  [BodyMetricSourceType.POLAR]: '#6b4c9a',
  [BodyMetricSourceType.OTHER]: '#595959',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const truncateUserId = (id: string) => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

export const BodyMetricLogList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';
  const [searchText, setSearchText] = useState('');

  const permanentFilters = useMemo(
    () =>
      [
        ...(userIdFromUrl
          ? [{ field: 'userId', operator: 'eq' as const, value: userIdFromUrl }]
          : []),
        ...(searchText
          ? [{ field: 'search', operator: 'contains' as const, value: searchText }]
          : []),
      ],
    [userIdFromUrl, searchText],
  );

  const { tableProps } = useTable<BodyMetricLogDto>({
    resource: 'body-metric-admin/logs',
    filters: {
      permanent: permanentFilters,
    },
    pagination: { pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="body-metric-admin/logs">
      {userIdFromUrl && (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          Filtered by user:{' '}
          <Link to={`/users/show/${userIdFromUrl}`}>{truncateUserId(userIdFromUrl)}</Link>
          {' · '}
          <Link to="/body-metric-logs">Clear filter</Link>
        </div>
      )}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by notes..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="userId"
          title="User ID"
          render={(id: string) => (
            <Link to={`/users/show/${id}`}>{truncateUserId(id)}</Link>
          )}
        />
        <Table.Column
          dataIndex="metricType"
          title="Metric Type"
          filters={Object.values(BodyMetricType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: BodyMetricLogDto) => record.metricType === value}
          render={(type: BodyMetricType) => (
            <Tag style={tagStyle(metricColors[type] || '#898989')}>{type}</Tag>
          )}
        />
        <Table.Column
          dataIndex="value"
          title="Value"
          render={(value: number, record: BodyMetricLogDto) => `${value} ${record.unit}`}
        />
        <Table.Column
          dataIndex="sourceType"
          title="Source Type"
          filters={Object.values(BodyMetricSourceType).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: BodyMetricLogDto) => record.sourceType === value}
          render={(source: BodyMetricSourceType) => (
            <Tag style={tagStyle(sourceColors[source] || '#898989')}>{source}</Tag>
          )}
        />
        <Table.Column
          dataIndex="recordedAt"
          title="Recorded At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: BodyMetricLogDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="body-metric-admin/logs" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};